import { Status } from "components/status";
import * as React from "react";
import styled from "styled-components";
import { theme } from "styles/theme";
import { TEstimate } from "types/estimate";
import { dollarFormatter } from "utils";
import { format, Locale } from "date-fns";
import { SubtleButton } from "components/button";
import { RiCheckboxCircleFill, RiArrowDownSLine, RiArrowRightSLine } from "react-icons/ri";
import Placeholder from "views/request/component/placeholder";
import useSeeMore from "hooks/useSeeMore";
import { useTranslation } from 'react-i18next';
import { enUS, es } from "date-fns/locale";
import { useAuthContext } from "contexts/auth";
import convertCVD from "utils/convertCVD";
import LineItemsContainer from "containers/lineItems";
import { useHistory } from "react-router-dom";

type Props = {
  data: TEstimate[] | null;
  type: "requests" | "projects";
  id: number | string;
  expandAll?: boolean;
  toggleAllTrigger?: number;
  onExpandedChange?: (allExpanded: boolean) => void;
};

type EstimateItemProps = {
  estimate: TEstimate;
  locale: Locale;
  cvd_type: string | null;
  tr: (key: string) => string;
  expanded: boolean;
  onToggleExpand: () => void;
  index: number;
};

function EstimateItem({
  estimate,
  locale,
  cvd_type,
  tr,
  expanded,
  onToggleExpand,
  index,
}: EstimateItemProps) {
  const history = useHistory();

  const wonOption = estimate.options?.find(opt => opt.selected);
  const defaultOptionId = wonOption ? wonOption.id : (estimate.options?.[0]?.id ?? null);
  const [selectedOptionId, setSelectedOptionId] = React.useState<number | null>(defaultOptionId);

  React.useEffect(() => {
    if (defaultOptionId && selectedOptionId === null) {
      setSelectedOptionId(defaultOptionId);
    }
  }, [defaultOptionId, selectedOptionId]);

  const statusColor = convertCVD(estimate.status.color, cvd_type);
  const estimateWon = estimate.status.id === 5;



  const populateOptions = estimate.options?.map((option) => {
    return (
      <div
        key={option.id}
        className={`
        border relative p-1 rounded flex flex-col ${option.selected && "!bg-green-600 border-green-700 !text-white"
          }`}
      >
        <span className="font-bold text-xs flex">
          <span>{option.label}</span>

          {option.selected && (
            <RiCheckboxCircleFill
              className="text-white-700 text-sm ml-1"
              style={{
                zIndex: 1,
              }}
            />
          )}
        </span>
        <span className="font-medium text-sm leading-none">
          {dollarFormatter(option.actual_total)}
        </span>
      </div>
    );
  });

  return (
    <SEstimateCon className={`${estimateWon && "!bg-green-100/50 !border-green-600"}`}>
      <SEstimateHeader onClick={() => history.push(`/estimates/${estimate.id}`)}>
        <div className="flex flex-1 min-w-0">
          <SArrow
            className="mr-3 mt-1"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            aria-label={expanded ? tr("Collapse") : tr("Expand")}
          >
            {expanded ? <RiArrowDownSLine /> : <RiArrowRightSLine />}
          </SArrow>
          <SEstimateIcon className="mr-3">{index}</SEstimateIcon>
          <div className="flex-1 min-w-0">
            <SId className="flex flex-wrap text-sm font-bold text-slate-500">
              <span>#{estimate.id}</span>{" "}
              ·{" "}
              {format(new Date(estimate.entry_date), "MMM dd, yyyy", {
                locale: locale,
              })}
            </SId>
            <SLabel className="flex items-center space-x-2 mt-1">
              <span className="font-semibold text-slate-800">{estimate.description}</span>{" "}
              <Status color={statusColor} size="small">
                {tr(estimate.status.label)}
              </Status>
            </SLabel>
            <div className="mt-2">
              <SOptions>{populateOptions}</SOptions>
            </div>
          </div>
        </div>
        {estimateWon && (
          <div className="text-green-600 text-2xl p-2">
            <RiCheckboxCircleFill />
          </div>
        )}
      </SEstimateHeader>

      {expanded && (
        <div className="p-4 border-t bg-slate-50/50 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-3 rounded border border-slate-100">
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">{tr("Created")}</span>
              <span className="text-sm font-semibold text-slate-700 mt-1">
                {format(new Date(estimate.entry_date), "MMM dd, yyyy")}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">{tr("Sent")}</span>
              <span className="text-sm font-semibold text-slate-700 mt-1">
                {estimate.send_date ? format(new Date(estimate.send_date), "MMM dd, yyyy") : <span className="text-amber-600">{tr("Not Sent")}</span>}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">{tr("Signed")}</span>
              <span className="text-sm font-semibold text-slate-700 mt-1">
                {estimate.respond_date ? format(new Date(estimate.respond_date), "MMM dd, yyyy") : <span className="text-slate-400">-</span>}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">{tr("Converted")}</span>
              <span className="text-sm font-semibold text-slate-700 mt-1">
                {estimate.converted_date ? format(new Date(estimate.converted_date), "MMM dd, yyyy") : <span className="text-slate-400">-</span>}
              </span>
            </div>
          </div>

          {estimate.declined_note && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">
              <span className="font-bold">{tr("Declined Note")}:</span> {estimate.declined_note}
            </div>
          )}

          {estimate.options && estimate.options.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2 border-b pb-2 flex-wrap gap-y-2">
                <span className="text-sm font-bold text-slate-700 mr-2">{tr("Estimate Options")}:</span>
                <div className="flex space-x-2 flex-wrap gap-y-2">
                  {estimate.options.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setSelectedOptionId(opt.id)}
                      className={`px-3 py-1 text-xs font-semibold rounded border transition-all ${selectedOptionId === opt.id
                        ? "bg-blue-50 border-blue-600 text-blue-700 font-bold"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                      {opt.label} ({dollarFormatter(opt.actual_total)})
                    </button>
                  ))}
                </div>
              </div>

              {selectedOptionId && (
                <div className="bg-white p-3 rounded border border-slate-200">
                  <LineItemsContainer
                    id={String(selectedOptionId)}
                    type="option"
                    noTax={false}
                    readOnly
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </SEstimateCon>
  );
}

export default function Estimates(props: Props) {
  const { t, i18n } = useTranslation();
  const tBase = "containers.estimatesListV2.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;
  const { cvd_type } = useAuthContext();

  const seeMore = useSeeMore(false);

  const [expandedIds, setExpandedIds] = React.useState<Record<string | number, boolean>>({});
  const lastToggleAllTrigger = React.useRef(props.toggleAllTrigger);
  const lastExpandAll = React.useRef(props.expandAll);

  const sortedData = React.useMemo(() => {
    if (!props.data) return [];
    return [...props.data].sort((a, b) => new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime());
  }, [props.data]);

  const allExpanded = React.useMemo(() => {
    if (sortedData.length === 0) return false;
    return sortedData.every((estimate) => expandedIds[estimate.id] ?? false);
  }, [sortedData, expandedIds]);

  React.useEffect(() => {
    if (props.onExpandedChange) {
      props.onExpandedChange(allExpanded);
    }
  }, [allExpanded, props.onExpandedChange]);

  React.useEffect(() => {
    if (props.toggleAllTrigger !== undefined && props.toggleAllTrigger !== lastToggleAllTrigger.current) {
      lastToggleAllTrigger.current = props.toggleAllTrigger;
      const targetState = !allExpanded;
      const nextExpanded: Record<string | number, boolean> = {};
      sortedData.forEach((estimate) => {
        nextExpanded[estimate.id] = targetState;
      });
      setExpandedIds(nextExpanded);
    }
  }, [props.toggleAllTrigger, sortedData, allExpanded]);

  React.useEffect(() => {
    if (props.expandAll !== undefined && props.expandAll !== lastExpandAll.current) {
      lastExpandAll.current = props.expandAll;
      const nextExpanded: Record<string | number, boolean> = {};
      sortedData.forEach((estimate) => {
        nextExpanded[estimate.id] = props.expandAll;
      });
      setExpandedIds(nextExpanded);
    }
  }, [props.expandAll, sortedData]);

  const toggleExpand = (id: string | number) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (!props.data) return null;

  const populateEstimates =
    sortedData.length > 0 ? (
      sortedData.slice(0, !seeMore.show ? 3 : undefined).map((estimate, i) => {
        return (
          <EstimateItem
            key={estimate.id}
            estimate={estimate}
            locale={locale}
            cvd_type={cvd_type}
            tr={tr}
            expanded={expandedIds[estimate.id] ?? false}
            onToggleExpand={() => toggleExpand(estimate.id)}
            index={sortedData.length - i}
          />
        );
      })
    ) : (
      <Placeholder
        type="estimate"
        header={tr("No estimates yet.")}
        label={tr("Estimates will appear here when added.")}
      />
    );
  return (
    <SCon>
      {populateEstimates}
      {props.data.length > 3 && (
        <SubtleButton primary onClick={seeMore.toggle}>
          {seeMore.show ? tr("Show Less") : tr("Show More")}
        </SubtleButton>
      )}
    </SCon>
  );
}

const SEstimateCon = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${theme.border.light};
  margin-bottom: 1em;
  position: relative;
  background: white;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${theme.border.dark};
  }
`;

const SEstimateHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75em;
  position: relative;
  cursor: pointer;
`;

const SArrow = styled.div`
  font-size: 1.5rem;
  line-height: 1;
  color: ${theme.color.subtext3};
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  transition: transform 0.2s ease;
`;



const SCon = styled.div``;

const SId = styled.span`
  text-transform: uppercase;
  font-size: ${theme.fontSize.small};
  color: ${theme.color.subtext2};
`;

const SLabel = styled.span`
  font-size: 1.2rem;
`;

const SOption = styled.div<{ selected: boolean }>``;

const SOptions = styled.div`
  display: flex;
  > div:not(:last-child) {
    margin-right: 0.5em;
  }
`;

const SEstimateIcon = styled.div`
  background: ${theme.bg.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 5px;
  font-size: 1.5rem;
  padding: 0.5rem;
  flex-shrink: 0;
`;
